import { useId } from "react";
import { FileText } from "lucide-react";
import { routePath } from "../../navigation/routes";
import { AppLink } from "../navigation/AppLink";
import { Popover, PopoverAnchor, PopoverContent } from "../ui/popover";
import { cn } from "../ui/utils";
import { CalendarViewLinks } from "./CalendarViewLinks";
import { PRICES_PLACE_TABS, PRICES_TAB_ICONS, pricesTabRoute, type PricesFormProps, type PricesPlaceTab } from "./prices-places";
import { RailDestination } from "./RailDestination";
import { useRailFlyout } from "./useRailFlyout";

interface PricesFlyoutProps extends PricesFormProps {
    /** The Prices page is on screen: the icon is the deepest link the folded rail draws for it. */
    active: boolean;
    /** A click on the icon itself. */
    onSelect: () => void;
}

const ROW =
    "flex h-8 w-full min-w-0 items-center gap-2 rounded-sm px-2 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50";

/**
 * The folded rail's Prices icon, with the page's tabs and calendar views in a flyout beside it in place of its
 * tooltip. The flyout has no trigger, so a click on the icon is still the plain way to the page.
 */
export function PricesFlyout({ active, onSelect, onOpenTab, onOpenView, ...form }: PricesFlyoutProps) {
    const { currentTab, shownView, labels, connectionId, connectionVersion, readTracking } = form;
    const flyout = useRailFlyout();
    const headingId = useId();
    const viewsHeadingId = useId();
    const pickTab = (tab: PricesPlaceTab) => {
        onOpenTab(tab);
        flyout.close({ focusAnchor: true });
    };

    return (
        <RailDestination
            page="drafts"
            label={labels.prices}
            Icon={FileText}
            collapsed
            active={active}
            onSelect={onSelect}
            linkProps={flyout.anchorProps}
            renderFolded={(link) => (
                <Popover open={flyout.open} onOpenChange={flyout.onOpenChange}>
                    <div className="relative">
                        <PopoverAnchor asChild>{link}</PopoverAnchor>
                        <span id={flyout.hintId} className="sr-only">
                            {labels.flyoutHint}
                        </span>
                    </div>
                    <PopoverContent
                        side="right"
                        align="start"
                        sideOffset={flyout.sideOffset}
                        collisionPadding={8}
                        role="group"
                        aria-labelledby={headingId}
                        className="w-72 max-h-(--radix-popover-content-available-height) overflow-y-auto rounded-lg p-1.5"
                        {...flyout.contentProps}
                    >
                        <p
                            id={headingId}
                            className="px-2 pt-1 pb-1.5 text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
                        >
                            {labels.prices}
                        </p>
                        <ul>
                            {PRICES_PLACE_TABS.map((tab) => {
                                const current = currentTab === tab;
                                const Icon = PRICES_TAB_ICONS[tab];
                                return (
                                    <li key={tab}>
                                        <AppLink
                                            to={routePath(pricesTabRoute(tab))}
                                            onNavigate={() => pickTab(tab)}
                                            aria-current={current ? "true" : undefined}
                                            data-rail-flyout-item=""
                                            className={cn(ROW, current ? "bg-accent text-primary font-medium" : "hover:bg-muted")}
                                        >
                                            <Icon
                                                aria-hidden="true"
                                                className={cn("size-4 shrink-0", current ? "text-primary" : "text-muted-foreground")}
                                            />
                                            <span className="truncate">{labels[tab]}</span>
                                        </AppLink>
                                        {tab === "calendar" && (
                                            <CalendarViewLinks
                                                layout="grid"
                                                heading={{ id: viewsHeadingId, text: labels.views }}
                                                shown={shownView}
                                                connectionId={connectionId}
                                                connectionVersion={connectionVersion}
                                                read={readTracking}
                                                fallback={null}
                                                onOpenView={(view) => {
                                                    onOpenView(view);
                                                    flyout.close({ focusAnchor: true });
                                                }}
                                            />
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </PopoverContent>
                </Popover>
            )}
        />
    );
}
